// Download Details Admin Section - Admin interface for individual download details

import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../../../components/Layout';
import { DownloadsManager } from '../../managers/downloads-manager';
import { useManager } from '../../../../core';
import { useAuth } from '../../../../contexts/AuthContext';
import { DownloadDetailsLayout } from '../details/components/DownloadDetailsLayout';
import { CreateEditDownloadModal } from '../../components';
import '../details/DownloadDetailsSection.css';
import './DownloadsAdminDetails.css';

export function DownloadDetailsAdminSection() {
  const { user } = useAuth();
  const { manager, isInitialized } = useManager(DownloadsManager);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [download, setDownload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const downloadId = searchParams.get('downloadId') || '';

  const loadDownload = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await manager.getDownloadById(id);
      setDownload(data);
    } catch (err) {
      console.error('Failed to load download:', err);
      setError(err.message || 'Failed to load download');
      setDownload(null);
    } finally {
      setLoading(false);
    }
  }, [manager]);

  useEffect(() => {
    if (isInitialized && downloadId) {
      loadDownload(downloadId);
    } else {
      setDownload(null);
      setLoading(false);
      setError(null);
    }
  }, [isInitialized, downloadId, loadDownload]);

  const handleBackToDownloads = useCallback((e) => {
    e.preventDefault();
    navigate('/downloads/admin');
  }, [navigate]);

  const handleBackToDetails = useCallback((e) => {
    e.preventDefault();
    navigate(`/downloads/details?downloadId=${downloadId}`);
  }, [navigate, downloadId]);


  const handleEditSubmit = useCallback(async () => {
    await loadDownload(downloadId);
    setIsEditModalOpen(false);
  }, [loadDownload, downloadId]);

  const handleResetTracking = useCallback((e) => {
    e.preventDefault();
    if (downloadId) {
      localStorage.removeItem(`download_views_${downloadId}`);
      localStorage.removeItem(`download_downloads_${downloadId}`);
      console.log(`Reset tracking for download: ${downloadId}`);
    }
  }, [downloadId]);

  const handleClearViews = useCallback(async (e) => {
    e.preventDefault();
    if (!downloadId || !manager) return;
    try {
      await manager.clearViews(downloadId);
      setDownload(prev => prev ? { ...prev, views: 0 } : prev);
    } catch (err) {
      console.error('Failed to clear views:', err);
    }
  }, [downloadId, manager]);

  const handleClearDownloads = useCallback(async (e) => {
    e.preventDefault();
    if (!downloadId || !manager) return;
    try {
      await manager.clearDownloads(downloadId);
      setDownload(prev => prev ? { ...prev, downloads: 0 } : prev);
    } catch (err) {
      console.error('Failed to clear downloads:', err);
    }
  }, [downloadId, manager]);

  if (!user || user.role !== 'admin') {
    return (
      <MainLayout contentClassName="main-content--download-details">
        <div className="download-details-section">
          <div className="access-denied">
            <i className="fa-solid fa-shield-alt" style={{ fontSize: '3rem', color: '#ff4444', marginBottom: '20px' }}></i>
            <h1>Access Denied</h1>
            <p>This admin section is only available to administrators.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout contentClassName="main-content--download-details">
      <div className="download-details-section">
        <div className="download-details-header">
          <div className="download-details-controls-row">
            <button
              className="downloads-admin-btn downloads-back-btn"
              onClick={handleBackToDownloads}
              title="Back to Downloads"
            >
              <i className="fa-solid fa-arrow-left"></i>
              Back to Downloads
            </button>
            <button
              className="downloads-admin-btn"
              onClick={handleBackToDetails}
              title="Back to Details (exit admin)"
            >
              <i className="fa-solid fa-arrow-left"></i>
              Back to Details
            </button>
            {download && (
              <button
                className="downloads-admin-btn downloads-edit-btn"
                onClick={() => setIsEditModalOpen(true)}
                title="Edit Download"
              >
                <i className="fa-solid fa-pen"></i>
                Edit
              </button>
            )}
            <button
              className="downloads-admin-btn downloads-reset-btn"
              onClick={handleResetTracking}
              title="Reset View/Download Tracking"
            >
              <i className="fa-solid fa-refresh"></i>
              Reset Tracking
            </button>
            <button
              className="downloads-admin-btn downloads-clear-btn"
              onClick={handleClearViews}
              title="Clear Views"
            >
              <i className="fa-solid fa-eye-slash"></i>
              Clear Views
            </button>
            <button
              className="downloads-admin-btn downloads-clear-btn"
              onClick={handleClearDownloads}
              title="Clear Downloads"
            >
              <i className="fa-solid fa-trash"></i>
              Clear Downloads
            </button>
          </div>
        </div>

        <div className="download-details-content">
          {loading ? (
            <div className="download-details-loading-state">
              <div className="download-details-loading-content">
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>Loading download...</span>
              </div>
            </div>
          ) : error ? (
            <div className="download-details-error-state">
              <div className="download-details-error-content">
                <i className="fa-solid fa-exclamation-triangle"></i>
                <h3>Failed to load download</h3>
                <p>{error}</p>
              </div>
            </div>
          ) : download ? (
            <DownloadDetailsLayout
              download={download}
            />
          ) : (
            <div className="download-details-error-state">
              <div className="download-details-error-content">
                <i className="fa-solid fa-exclamation-triangle"></i>
                <h3>Download not found</h3>
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateEditDownloadModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        download={download}
      />
    </MainLayout>
  );
}
